import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=unauthenticated, object=logged in

  useEffect(() => {
    const me = () => axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
    me()
      .then((r) => setUser(r.data))
      .catch(async (err) => {
        if (err.response?.status === 401) {
          // Access token may have expired — attempt one silent refresh
          try {
            await axios.post(`${BACKEND_URL}/api/auth/refresh`, null, { withCredentials: true });
            const r = await me();
            setUser(r.data);
            return;
          } catch (e) {
            console.debug("Auth silent-refresh failed, treating as logged out:", e?.message);
          }
        }
        setUser(false);
      });
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password }, { withCredentials: true });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, null, { withCredentials: true });
    } catch (e) {
      console.debug("Logout request failed (clearing session anyway):", e?.message);
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, login, logout, isAdmin: user && user.role === "admin" }),
    [user, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
